import React, { useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  TablePagination,
  CircularProgress,
  Typography,
  Box,
  Button,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { keyframes } from "@mui/system";
import { GetApp as DownloadIcon } from "@mui/icons-material";
import { SuppliersProvider, useSuppliers } from "../../contexts/albina/SuppliersContext";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// ===== Animations & Styles =====
const floatIn = keyframes`
  0% { opacity: 0; transform: translateY(12px) scale(0.98); }
  100% { opacity: 1; transform: translateY(0) scale(1); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const Root = styled("div")(({ theme }) => ({
  padding: theme.spacing(3),
  background: "linear-gradient(180deg, #f8fafc, #eef2ff)",
  minHeight: "100vh",
}));

const Title = styled("h2")(({ theme }) => ({
  margin: 0,
  fontSize: "1.8rem",
  fontWeight: 700,
  background: "linear-gradient(90deg, #06b6d4, #6366f1, #4f46e5)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  animation: `${shimmer} 4s linear infinite`,
}));

const GlassPaper = styled(Paper)(({ theme }) => ({
  borderRadius: 14,
  background: "rgba(255,255,255,0.7)",
  boxShadow: "0 8px 30px rgba(15,23,42,0.12)",
  backdropFilter: "blur(8px)",
  animation: `${floatIn} 600ms ease both`,
}));

const GradientButton = styled(Button)(({ theme }) => ({
  textTransform: "none",
  fontWeight: 600,
  borderRadius: 10,
  padding: theme.spacing(1, 2),
  background: "linear-gradient(90deg, #06b6d4, #6366f1)",
  color: "#fff",
  boxShadow: "0 4px 14px rgba(99,102,241,0.3)",
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 24px rgba(79,70,229,0.4)',
  },
}));

// ===== Component =====
const SuppliersTable = () => {
  const { suppliers, loading, error, classifiedPurchaseOrders } = useSuppliers();

  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const filteredSuppliers = useMemo(() => {
    if (!suppliers) return [];
    return suppliers.filter((supplier) =>
      Object.values(supplier).some((value) =>
        value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [suppliers, searchTerm]);

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const exportToExcel = () => {
    const date = new Date().toISOString().split('T')[0].replace(/-/g, '');
    const wb = XLSX.utils.book_new();

    const suppliersData = filteredSuppliers.map(sup => ({
      Ref: `#${sup._id.slice(0, 8)}`,
      Name: sup.name,
      Country: sup.country,
      Code: sup.code,
      Emails: sup.emails.length ? sup.emails.map(item => item.email).join(', ') : 'Empty'
    }));
    const wsSuppliers = XLSX.utils.json_to_sheet(suppliersData);
    XLSX.utils.book_append_sheet(wb, wsSuppliers, 'Suppliers');

    const classifiedData = classifiedPurchaseOrders?.map(item => ({
      'Supplier ID': item.orders?.flatMap(order => order.po_inv_items)?.find(poItem => poItem.supplier)?.supplier || 'N/A',
      Total: item?.total?.toFixed(2) || 0,
      'Number of Orders': item.orders.length || 0
    })) || [];
    const wsClassified = XLSX.utils.json_to_sheet(classifiedData);
    XLSX.utils.book_append_sheet(wb, wsClassified, 'PurchaseOrdersBySuppliers');

    XLSX.writeFile(wb, `Suppliers_Export_${date}.xlsx`);
  };

  const exportToPDF = () => {
    const date = new Date().toISOString().split("T")[0].replace(/-/g, "");
    const doc = new jsPDF();

    doc.text(`Suppliers List ${new Date().toLocaleDateString()}`, 20, 10);
    const suppliersTable = filteredSuppliers.map((sup) => [
      `#${sup._id.slice(0, 8)}`,
      sup.name,
      sup.country,
      sup.code,
      sup.emails.length ? sup.emails.map((item) => item.email).join(", ") : "Empty",
    ]);

    autoTable(doc, { head: [["Ref", "Name", "Country", "Code", "Emails"]], body: suppliersTable, startY: 20 });

    doc.addPage();
    doc.text("Purchase Orders by Suppliers", 20, 10);
    const classifiedTable = classifiedPurchaseOrders?.map((item) => [
      item.orders?.flatMap((order) => order.po_inv_items)?.find((poItem) => poItem.supplier)?.supplier || "N/A",
      item?.total?.toFixed(2) || 0,
      item.orders.length || 0,
    ]) || [];

    autoTable(doc, { head: [["Supplier ID", "Total", "Number of Orders"]], body: classifiedTable, startY: 20 });
    doc.save(`Suppliers_Export_${date}.pdf`);
  };

  if (loading) {
    return <Box display="flex" justifyContent="center" alignItems="center" height="200px"><CircularProgress /></Box>;
  }
  if (error) {
    return <Typography color="error" variant="h6">Error: {error}</Typography>;
  }

  return (
    <Root>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Title>Suppliers List</Title>
        <Box display="flex" gap={1}>
          <GradientButton startIcon={<DownloadIcon />} onClick={exportToExcel}>Export Excel</GradientButton>
          <GradientButton startIcon={<DownloadIcon />} onClick={exportToPDF}>Export PDF</GradientButton>
        </Box>
      </Box>

      <TextField label="Search" variant="outlined" fullWidth margin="normal" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />

      <TableContainer component={GlassPaper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Ref</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Country</TableCell>
              <TableCell>Code</TableCell>
              <TableCell>Emails</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredSuppliers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((supplier, index) => (
              <TableRow key={supplier.id || index} hover>
                <TableCell>#{supplier._id.slice(0, 8)}</TableCell>
                <TableCell>{supplier.name}</TableCell>
                <TableCell>{supplier.country}</TableCell>
                <TableCell>{supplier.code}</TableCell>
                <TableCell>
                  {supplier.emails.length ? supplier.emails.map((item) => item.email).join(', ') : "Empty"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination rowsPerPageOptions={[5, 10, 25]} component="div" count={filteredSuppliers.length} rowsPerPage={rowsPerPage} page={page} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} />
    </Root>
  );
};

const Suppliers = () => (
  <SuppliersProvider>
    <SuppliersTable />
  </SuppliersProvider>
);

export default Suppliers;
